import { useEffect, useState }                  from 'react'
import { BlockStack, Card, InlineGrid, InlineStack, Page, Spinner, Text, Badge } from '@shopify/polaris'
import { useNavigate }                           from 'react-router-dom'
import { getHistoryStats }                       from '../../../sdk/history.api'
import type { HistoryStatsResponse, QueueActivityTab } from '../../../sdk/history.api'

const WORKFLOWS: { tab: QueueActivityTab; label: string; href: string }[] = [
  { tab: 'CF_Activity', label: 'Confirmation',         href: '/boarding/history/cf'  },
  { tab: 'SC_Activity', label: 'Shipping Confirmation', href: '/boarding/history/sc'  },
  { tab: 'ST_Activity', label: 'Sales Team',           href: '/boarding/history/st'  },
  { tab: 'CS_Activity', label: 'Customer Service',     href: '/boarding/history/cs'  },
]

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

type WorkflowSummary = {
  tab:        QueueActivityTab
  label:      string
  href:       string
  stats:      HistoryStatsResponse | null
  loading:    boolean
  error:      string | null
}

export function HistoryOverviewPage() {
  const navigate = useNavigate()
  const date     = todayIso()

  const [summaries, setSummaries] = useState<WorkflowSummary[]>(
    WORKFLOWS.map(w => ({ ...w, stats: null, loading: true, error: null })),
  )

  useEffect(() => {
    WORKFLOWS.forEach((w, idx) => {
      getHistoryStats(w.tab, date)
        .then(stats => {
          setSummaries(prev => prev.map((s, i) => i === idx ? { ...s, stats, loading: false } : s))
        })
        .catch(() => {
          setSummaries(prev => prev.map((s, i) => i === idx ? { ...s, loading: false, error: 'Failed to load' } : s))
        })
    })
  }, [date])

  return (
    <Page
      title="History"
      subtitle={`Activity overview for ${date}`}
      backAction={{ content: 'Boarding', url: '/boarding' }}
    >
      <BlockStack gap="600">

        {/* Cross-workflow summary cards */}
        <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
          {summaries.map((s) => {
            const totalSessions   = s.stats?.agents.reduce((acc, a) => acc + a.completedSessions, 0) ?? 0
            const totalAgents     = s.stats?.agents.length ?? 0
            const anomalyCount    = s.stats?.anomalies.length ?? 0
            const topAgent        = s.stats?.leaderboard[0]

            return (
              <Card key={s.tab}>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h3" variant="headingSm">{s.label}</Text>
                    {anomalyCount > 0 && (
                      <Badge tone="warning">{`${anomalyCount} ${anomalyCount === 1 ? 'flag' : 'flags'}`}</Badge>
                    )}
                  </InlineStack>

                  {s.loading && <Spinner size="small" />}
                  {s.error   && <Text as="p" variant="bodySm" tone="critical">{s.error}</Text>}

                  {!s.loading && !s.error && (
                    <InlineGrid columns={3} gap="200">
                      <BlockStack gap="050">
                        <Text as="p" variant="bodySm" tone="subdued">Active agents</Text>
                        <Text as="p" variant="headingMd">{totalAgents}</Text>
                      </BlockStack>
                      <BlockStack gap="050">
                        <Text as="p" variant="bodySm" tone="subdued">Sessions done</Text>
                        <Text as="p" variant="headingMd">{totalSessions}</Text>
                      </BlockStack>
                      <BlockStack gap="050">
                        <Text as="p" variant="bodySm" tone="subdued">Top agent</Text>
                        <Text as="p" variant="headingMd">{topAgent?.agent ?? '—'}</Text>
                      </BlockStack>
                    </InlineGrid>
                  )}

                  <InlineStack align="end">
                    <button
                      onClick={() => navigate(s.href)}
                      style={{
                        padding: '6px 16px', borderRadius: '8px',
                        border: '1px solid var(--p-color-border)',
                        background: 'transparent', color: 'var(--p-color-text)',
                        cursor: 'pointer', fontSize: '14px',
                      }}
                    >
                      View details
                    </button>
                  </InlineStack>
                </BlockStack>
              </Card>
            )
          })}
        </InlineGrid>

      </BlockStack>
    </Page>
  )
}
